import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'react-icons/fa';
import DeleteButton from '../editors/DeleteButton';

interface WhyUsPoint {
  icon: string;
  number: string;
  startNumber: number;
  endNumber: number;
  duration: number;
  ar: string;
  en: string;
  color: string;
  iconColor: string;
}

interface AboutWhyUsSectionProps {
  language?: 'ar' | 'en';
  points?: WhyUsPoint[];
  backgroundColor?: string;
  titleAr?: string;
  titleEn?: string;
  showSection?: boolean;
  layout?: 'horizontal' | 'grid';
  onDeletePoint?: (index: number) => void; // إضافة دالة حذف النقطة
}

const AboutWhyUsSection: React.FC<AboutWhyUsSectionProps> = ({
  language = 'ar',
  points = [
    { 
      icon: "FaAward", 
      number: "11+", 
      startNumber: 0, 
      endNumber: 11, 
      duration: 2000,
      ar: "سنة خبرة", 
      en: "Years Experience", 
      color: "bg-blue-500",
      iconColor: "#ffffff"
    },
    { 
      icon: "FaUsers", 
      number: "50+", 
      startNumber: 0, 
      endNumber: 50, 
      duration: 2000,
      ar: "مهندس محترف", 
      en: "Professional Engineers", 
      color: "bg-green-500",
      iconColor: "#ffffff"
    },
    { 
      icon: "FaBuilding", 
      number: "200+", 
      startNumber: 0, 
      endNumber: 200, 
      duration: 2000,
      ar: "مشروع منجز", 
      en: "Completed Projects", 
      color: "bg-purple-500",
      iconColor: "#ffffff"
    },
    { 
      icon: "FaHeart", 
      number: "98%", 
      startNumber: 0, 
      endNumber: 98, 
      duration: 2000,
      ar: "رضا العملاء", 
      en: "Client Satisfaction", 
      color: "bg-red-500",
      iconColor: "#ffffff"
    },
    { 
      icon: "FaShieldAlt", 
      number: "100%", 
      startNumber: 0, 
      endNumber: 100, 
      duration: 2000,
      ar: "جودة معتمدة", 
      en: "Certified Quality", 
      color: "bg-indigo-500",
      iconColor: "#ffffff"
    }
  ],
  backgroundColor = "from-blue-50 to-gray-100",
  titleAr = "لماذا نحن؟",
  titleEn = "Why Choose Us?",
  showSection = true,
  layout = "grid",
  onDeletePoint
}) => {
  const isRTL = language === 'ar';
  const [counters, setCounters] = useState<number[]>(points.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'FaAward': return Icons.FaAward;
      case 'FaUsers': return Icons.FaUsers;
      case 'FaBuilding': return Icons.FaBuilding;
      case 'FaHeart': return Icons.FaHeart;
      case 'FaShieldAlt': return Icons.FaShieldAlt;
      default: return Icons.FaAward;
    }
  };

  // Counter animation effect
  useEffect(() => {
    const startCounters = () => {
      points.forEach((point, index) => {
        const startTime = Date.now();
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / point.duration, 1);
          const currentValue = Math.floor(
            point.startNumber + (point.endNumber - point.startNumber) * progress
          );

          setCounters(prev => {
            const newCounters = [...prev];
            newCounters[index] = currentValue;
            return newCounters;
          });

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        animate();
      });
    };

    if (!hasAnimated && sectionRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setHasAnimated(true);
              startCounters();
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(sectionRef.current);
      return () => observer.disconnect();
    }
  }, [hasAnimated, points]);

  if (!showSection) return null;

  return (
    <section 
      ref={sectionRef}
      className={`section-padding bg-gradient-to-br ${backgroundColor}`}
      data-editable="section"
    >
      <div className="container-custom">
        <h2 
          className="text-3xl font-bold text-center mb-12 text-gray-900" 
          data-editable="text"
          data-text-key="titleAr"
        >
          {isRTL ? titleAr : titleEn}
        </h2>
        
        {layout === 'horizontal' ? (
          // Horizontal layout (like the blue bar in the image)
          <div className="bg-blue-600 rounded-xl p-8 text-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {points.map((point, idx) => {
                const IconComponent = getIconComponent(point.icon);
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="text-center"
                    data-editable="counter"
                  >
                    <div 
                      className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: point.color }}
                      data-editable="icon"
                    >
                      <IconComponent 
                        className="w-8 h-8 text-white" 
                        style={{ color: point.iconColor }}
                      />
                    </div>
                    <div className="text-3xl font-bold mb-2">
                      {hasAnimated ? counters[idx] : point.startNumber}
                      {point.number.includes('+') ? '+' : ''}
                      {point.number.includes('%') ? '%' : ''}
                    </div>
                    <div className="text-sm opacity-90" data-editable="text" data-text-key={`points.${idx}.ar`}>
                      {isRTL ? point.ar : point.en}
                    </div>
                    {onDeletePoint && (
                      <DeleteButton onDelete={() => onDeletePoint(idx)} />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        ) : (
          // Grid layout (original)
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {points.map((point, idx) => {
              const IconComponent = getIconComponent(point.icon);
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow"
                  data-editable="counter"
                >
                  <div 
                    className={`w-12 h-12 ${point.color} rounded-full flex items-center justify-center mx-auto mb-4`}
                    data-editable="icon"
                  >
                    <IconComponent 
                      className="w-6 h-6 text-white" 
                      style={{ color: point.iconColor }}
                    />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-2">
                    {hasAnimated ? counters[idx] : point.startNumber}
                    {point.number.includes('+') ? '+' : ''}
                    {point.number.includes('%') ? '%' : ''}
                  </div>
                  <div className="text-sm text-gray-600" data-editable="text" data-text-key={`points.${idx}.ar`}>
                    {isRTL ? point.ar : point.en}
                  </div>
                  {onDeletePoint && (
                    <DeleteButton onDelete={() => onDeletePoint(idx)} />
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutWhyUsSection; 